"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import NextImage from "next/image";
import { Navbar, Footer } from "@/components/layout/NavbarFooter";
import MapSection from "@/components/ui/MapSection";
import ApplicationModal from "@/components/ui/ApplicationModal";
import ContactModal from "@/components/ui/ContactModal";
import { useLanguage } from "@/context/LanguageContext";
import { jobService } from "@/services/jobService";
import { Job } from "@/types";
import { careerValues } from "@/data/careersData";
import CareersHeroScene from "@/components/ui/CareersHeroScene";
import { formatDate } from "@/utils/dateFormatter";
import { Link2 } from "lucide-react";
import { toast } from "react-hot-toast";

export default function CareersPageClient() {
  const router = useRouter();
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(
    undefined,
  );
  const [selectedJobTitle, setSelectedJobTitle] = useState<string | undefined>(
    undefined,
  );
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const openModal = (jobId: string, jobTitle: string) => {
    setSelectedJobId(jobId);
    setSelectedJobTitle(jobTitle);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJobId(undefined);
    setSelectedJobTitle(undefined);
  };

  const handleCopyJobLink = (slug: string) => {
    if (typeof window !== "undefined") {
      const url = `${window.location.origin}/careers/${slug}`;
      navigator.clipboard.writeText(url);
      toast.success(t("common.copied") || "Đã sao chép liên kết!");
    }
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        const data = await jobService.getJobPostings({ status: "open" });
        setJobs(data);
      } catch (err: any) {
        setError(err.message || "Failed to load job postings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const filteredJobs = jobs;

  useEffect(() => {
    if (isLoading) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    }, observerOptions);

    const timeoutId = setTimeout(() => {
      const revealElements = document.querySelectorAll(".reveal");
      revealElements.forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [isLoading]);

  return (
    <>
      <Navbar />
      <main className="bg-[#040d21] text-white antialiased overflow-hidden">
        {/* Career Hero Section */}
        <section className="relative min-h-[70vh] flex items-center pt-32 pb-24 overflow-hidden">
          {/* Dynamic Three.js Background */}
          <CareersHeroScene />

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-2 items-center gap-16">
              <div className="reveal reveal-up">
                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0a192f]/60 backdrop-blur-md text-[#00f3ff] font-black text-[10px] uppercase tracking-[0.2em] mb-8 border border-[#00f3ff]/20">
                  <span className="material-symbols-outlined text-sm">
                    rocket_launch
                  </span>
                  {t("careersPage.hero.badge")}
                </div>
                <h1 className="text-5xl lg:text-7xl font-black tracking-tighter mb-8 leading-tight text-white">
                  {t("careersPage.hero.title")}{" "}
                  <span className="text-[#00f3ff] italic drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                    AHV Holding
                  </span>
                </h1>
                <p className="text-xl text-slate-300 font-light leading-relaxed mb-10 max-w-xl">
                  {t("careersPage.hero.subtitle")}
                </p>
                <div className="flex flex-wrap gap-5">
                  <a
                    href="#positions"
                    className="bg-[#00f3ff] text-[#040d21] px-10 py-5 rounded-2xl font-black text-lg hover:shadow-[0_0_30px_rgba(0,243,255,0.6)] hover:bg-[#00f3ff]/90 transition-all hover:-translate-y-1 text-center"
                  >
                    {t("careersPage.hero.viewPositions")}
                  </a>
                  <a
                    href="#why-us"
                    className="bg-[#0a192f]/60 backdrop-blur-md text-white border border-[#00f3ff]/50 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-[#00f3ff]/20 transition-all text-center"
                  >
                    {t("careersPage.hero.whyUs")}
                  </a>
                </div>
              </div>
              <div className="relative hidden lg:block reveal reveal-scale">
                <div className="relative bg-[#0a192f]/80 backdrop-blur-xl p-4 rounded-[2.5rem] border border-[#00f3ff]/20 shadow-2xl overflow-hidden group">
                  <NextImage
                    alt="Văn hóa làm việc tại AHV Holding - Môi trường sáng tạo và năng động"
                    className="w-full h-[450px] object-cover rounded-[2rem] transition-all duration-700 group-hover:scale-105"
                    src="/about_us.jpg"
                    width={800}
                    height={450}
                    priority
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#040d21]/60 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="py-32 bg-[#040d21]" id="why-us">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-3xl mb-24 reveal reveal-up">
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-6 text-white">
                {t("careersPage.values.title")}
              </h2>
              <div className="h-1.5 w-24 bg-[#00f3ff] rounded-full mb-8 shadow-[0_0_10px_#00f3ff]"></div>
              <p className="text-slate-300 text-xl font-light leading-relaxed">
                {t("careersPage.values.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {careerValues.map((val, idx) => (
                <div
                  key={val.id}
                  className={`reveal reveal-scale delay-${(idx + 1) * 100} group bg-[#0a192f]/60 backdrop-blur-xl p-12 rounded-[3.5rem] border border-[#00f3ff]/20 hover:border-[#00f3ff]/60 hover:shadow-[0_0_30px_rgba(0,243,255,0.15)] transition-all duration-500 hover:-translate-y-4 shadow-xl shadow-black/40`}
                >
                  <div className="w-20 h-20 bg-[#00f3ff]/10 rounded-3xl flex items-center justify-center text-[#00f3ff] mb-10 group-hover:rotate-6 group-hover:scale-110 group-hover:bg-[#00f3ff] group-hover:text-[#040d21] group-hover:shadow-[0_0_20px_#00f3ff] transition-all duration-500 border border-[#00f3ff]/30">
                    <span className="material-symbols-outlined text-4xl group-hover:drop-shadow-none drop-shadow-[0_0_8px_currentColor]">
                      {val.icon}
                    </span>
                  </div>
                  <h3 className="text-2xl font-black mb-6 text-white group-hover:text-[#00f3ff] transition-colors drop-shadow-sm">
                    {t(`careersPage.values.items.${val.id}.title`) || val.title}
                  </h3>
                  <p className="text-slate-400 text-lg leading-relaxed font-medium">
                    {t(`careersPage.values.items.${val.id}.description`) ||
                      val.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Listings Board */}
        <section className="py-32 bg-[#0a192f]" id="positions">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-10 mb-20 reveal reveal-up">
              <div className="max-w-xl">
                <h2 className="text-4xl font-black mb-4 tracking-tighter text-white">
                  {t("careersPage.positions.title")}
                </h2>
                <p className="text-slate-400 text-lg font-light">
                  {t("careersPage.positions.subtitle")}
                </p>
              </div>
            </div>

            <div className="space-y-6 mb-20 font-sans">
              {isLoading ? (
                <div className="flex justify-center py-20">
                  <div className="w-12 h-12 border-4 border-[#00f3ff]/20 border-t-[#00f3ff] rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="text-center py-10 bg-red-500/10 border border-red-500/20 rounded-2xl">
                  <p className="text-red-400 font-bold">{error}</p>
                </div>
              ) : filteredJobs.length > 0 ? (
                filteredJobs.map((job, idx) => (
                  <div
                    key={job.id}
                    className={`animate-fade-in group bg-[#040d21]/80 backdrop-blur-xl rounded-[2.5rem] border border-[#00f3ff]/20 p-8 lg:p-12 transition-all hover:border-[#00f3ff]/50 hover:shadow-[0_0_30px_rgba(0,243,255,0.1)] hover:-translate-y-2 flex flex-col md:flex-row md:items-center justify-between gap-8`}
                  >
                    <div className="flex flex-col gap-5">
                      <div className="flex flex-wrap items-center gap-5">
                        <h3
                          onClick={() => router.push(`/careers/${job.slug}`)}
                          className="text-2xl font-black text-white group-hover:text-[#00f3ff] transition-colors drop-shadow-sm cursor-pointer"
                        >
                          {job.title}
                        </h3>
                        <span className="px-4 py-1.5 bg-[#00f3ff]/10 text-[#00f3ff] border border-[#00f3ff]/20 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] drop-shadow-[0_0_2px_currentColor]">
                          {job.type}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-8 text-slate-400 text-sm font-bold tracking-widest uppercase opacity-80">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-lg text-[#00f3ff]">
                            location_on
                          </span>
                          {job.location}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-lg text-[#00f3ff]">
                            payments
                          </span>
                          {t("careersPage.positions.salary")}
                        </div>
                        {job.deadline && (
                          <div className="flex items-center gap-2 text-brand-primary">
                            <span className="material-symbols-outlined text-lg">
                              calendar_clock
                            </span>
                            {t("careersPage.positions.deadline")}: {formatDate(job.deadline)}
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => router.push(`/careers/${job.slug}`)}
                            className="text-[#00f3ff] text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer flex items-center gap-1 group/btn"
                          >
                            {t("careersPage.positions.viewDetails")}{" "}
                            <span className="material-symbols-outlined text-sm transition-transform group-hover/btn:translate-x-1">
                              arrow_forward
                            </span>
                          </button>
                          <button
                            onClick={() => handleCopyJobLink(job.slug)}
                            className="text-slate-500 hover:text-[#00f3ff] transition-colors cursor-pointer flex items-center gap-1"
                            title={t("careersPage.detail.copyLink") || "Sao chép liên kết"}
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => openModal(job.id, job.title)}
                      className="whitespace-nowrap px-10 py-5 rounded-2xl bg-[#00f3ff] text-[#040d21] font-black uppercase tracking-widest text-sm hover:shadow-[0_0_30px_#00f3ff] transition-all hover:-translate-y-1 cursor-pointer"
                    >
                      {t("careersPage.positions.applyNow")}
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-20 text-slate-500 italic">
                  {t("careersPage.positions.noJobsFound")}
                </div>
              )}
            </div>

            <div className="text-center reveal reveal-up">
              <p className="text-slate-400 text-lg font-light mb-8 italic">
                {t("careersPage.positions.noFit")}
              </p>
              <button
                onClick={() => openModal("", "General Application")}
                className="px-12 py-5 rounded-2xl border-2 border-[#00f3ff]/30 text-[#00f3ff] font-black uppercase tracking-[0.2em] text-xs hover:bg-[#00f3ff] hover:text-[#040d21] hover:shadow-[0_0_30px_#00f3ff] transition-all cursor-pointer"
              >
                {t("careersPage.positions.uploadCV")}
              </button>
            </div>
          </div>
        </section>

        {/* Careers CTA */}
        <section className="py-32 px-6 bg-[#040d21]">
          <div className="max-w-6xl mx-auto rounded-[3.5rem] overflow-hidden relative p-12 lg:p-24 text-center bg-[#0a192f]/60 backdrop-blur-xl group shadow-[0_0_50px_rgba(0,0,0,0.8)] border border-[#00f3ff]/20">
            {/* Ambient Background Flow */}
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(0,243,255,0.05),transparent_70%)]"></div>
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#00f3ff]/5 rounded-full blur-[120px] animate-pulse-slow"></div>

            <div className="relative z-10 max-w-2xl mx-auto reveal reveal-up">
              <h2 className="text-4xl lg:text-6xl font-black mb-8 tracking-tighter text-white leading-[0.95]">
                {t("careersPage.cta.title")}{" "}
                <span className="text-[#00f3ff] drop-shadow-[0_0_8px_rgba(0,243,255,0.4)]">
                  {t("careersPage.cta.titleAccent")}
                </span>
              </h2>
              <p className="text-slate-300 text-lg mb-12 font-light leading-relaxed">
                {t("careersPage.cta.subtitle")}
              </p>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="bg-[#00f3ff] text-[#040d21] px-12 py-5 rounded-2xl font-black uppercase tracking-widest text-lg hover:shadow-[0_0_30px_#00f3ff] transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                {t("careersPage.cta.explore")}
              </button>
            </div>
          </div>
        </section>

        <MapSection />
      </main>

      <ApplicationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        jobId={selectedJobId}
        jobTitle={selectedJobTitle}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <Footer />
    </>
  );
}
